// frontend/src/components/Dashboard/TopProducts.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faStarHalfAlt,
  faStar as faStarEmpty,
  faBox,
  faDollarSign,
  faShoppingCart,
  faTrendUp,
  faExclamationTriangle,
  faImage,
  faSpinner,
  faGlobe,
  faWeightScale,
  faBoxes,
  faLocationDot,
  faShip,
  faArrowUp,
  faArrowDown,
  faBuilding,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
};

const TopProducts = ({ products, isLoading = false }) => {
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [sortBy, setSortBy] = useState("revenue");
  const [sortDirection, setSortDirection] = useState("desc");

  if (isLoading) {
    return (
      <div className="top-products-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <p>Loading top products...</p>
      </div>
    );
  }

  let productData = products && products.length > 0 ? products : [];

  if (productData.length === 0) {
    return (
      <div className="top-products-empty">
        <div className="empty-icon">
          <FontAwesomeIcon icon={faBox} size="3x" />
        </div>
        <h3>No Product Data Yet</h3>
        <p>Start selling to see your top-performing products here.</p>
      </div>
    );
  }

  const sortProducts = (data) => {
    const sorted = [...data];
    sorted.sort((a, b) => {
      let aVal = a[sortBy] || 0;
      let bVal = b[sortBy] || 0;
      if (sortBy === "name") {
        aVal = (a.name || "").toLowerCase();
        bVal = (b.name || "").toLowerCase();
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDirection === "desc" ? bVal - aVal : aVal - bVal;
    });
    return sorted;
  };

  const sortedProducts = sortProducts(productData);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDirection("desc");
    }
  };

  const renderStars = (rating) => {
    if (!rating || rating === 0)
      return <span className="no-rating">No ratings yet</span>;

    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <>
        {[...Array(fullStars)].map((_, i) => (
          <FontAwesomeIcon
            key={`full-${i}`}
            icon={faStar}
            className="star filled"
          />
        ))}
        {hasHalfStar && (
          <FontAwesomeIcon
            key="half"
            icon={faStarHalfAlt}
            className="star half-filled"
          />
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <FontAwesomeIcon
            key={`empty-${i}`}
            icon={faStarEmpty}
            className="star empty"
          />
        ))}
        <span className="rating-value">{rating.toFixed(1)}</span>
      </>
    );
  };

  const formatCurrency = (amount) => `$${amount?.toLocaleString() || "0"}`;

  const getProductImage = (product) => {
    if (product.image) return product.image;
    if (product.thumbnail) return product.thumbnail;
    if (product.images && product.images[0]) return product.images[0];
    return null;
  };

  const toggleExpand = (productId) => {
    setExpandedProduct(expandedProduct === productId ? null : productId);
  };

  const totalRevenue = productData.reduce(
    (sum, p) => sum + (p.revenue || 0),
    0,
  );

  const originCounts = productData.reduce((acc, p) => {
    const origin = p.origin || SUPPLIER.country;
    acc[origin] = (acc[origin] || 0) + 1;
    return acc;
  }, {});

  const getSortIcon = (field) => {
    if (sortBy === field) {
      return sortDirection === "asc" ? faArrowUp : faArrowDown;
    }
    return null;
  };

  return (
    <div className="top-products">
      <div className="top-products-header">
        <div className="header-left">
          <FontAwesomeIcon icon={faBox} className="header-icon" />
          <h3>Best Selling Products</h3>
          <span className="product-count">
            {productData.length} product{productData.length > 1 ? "s" : ""}
          </span>
        </div>
        <div className="header-right">
          <span className="origin-summary">
            <FontAwesomeIcon icon={faGlobe} />
            {Object.entries(originCounts).map(([origin, count]) => (
              <span
                key={origin}
                className={`origin-tag ${origin === SUPPLIER.country ? "malaysia" : ""}`}
              >
                {origin}: {count}
              </span>
            ))}
          </span>
          <span className="supplier-badge">
            <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
          </span>
        </div>
      </div>

      <div className="sort-controls">
        <span className="sort-label">Sort by:</span>
        <button
          className={`sort-btn ${sortBy === "revenue" ? "active" : ""}`}
          onClick={() => handleSort("revenue")}
        >
          Revenue{" "}
          {getSortIcon("revenue") && (
            <FontAwesomeIcon icon={getSortIcon("revenue")} />
          )}
        </button>
        <button
          className={`sort-btn ${sortBy === "sold" ? "active" : ""}`}
          onClick={() => handleSort("sold")}
        >
          Units Sold{" "}
          {getSortIcon("sold") && (
            <FontAwesomeIcon icon={getSortIcon("sold")} />
          )}
        </button>
        <button
          className={`sort-btn ${sortBy === "rating" ? "active" : ""}`}
          onClick={() => handleSort("rating")}
        >
          Rating{" "}
          {getSortIcon("rating") && (
            <FontAwesomeIcon icon={getSortIcon("rating")} />
          )}
        </button>
        <button
          className={`sort-btn ${sortBy === "name" ? "active" : ""}`}
          onClick={() => handleSort("name")}
        >
          Name{" "}
          {getSortIcon("name") && (
            <FontAwesomeIcon icon={getSortIcon("name")} />
          )}
        </button>
      </div>

      <div className="products-list">
        {sortedProducts.map((product, index) => {
          const revenue = product.revenue || 0;
          const percentage =
            totalRevenue > 0 ? (revenue / totalRevenue) * 100 : 0;
          const imageUrl = getProductImage(product);
          const isExpanded = expandedProduct === product._id;
          const origin = product.origin || SUPPLIER.country;
          const isMalaysia = origin === SUPPLIER.country;

          return (
            <div
              key={product._id || index}
              className={`product-item ${isExpanded ? "expanded" : ""} ${isMalaysia ? "malaysia-origin" : ""}`}
              onClick={() => toggleExpand(product._id)}
            >
              <div className="product-rank">
                <span className="rank-number">{index + 1}</span>
                {index === 0 && <span className="rank-crown">👑</span>}
              </div>

              <div className="product-avatar">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={product.name || "Product"}
                    onError={(e) => {
                      e.target.src = "";
                      e.target.style.display = "none";
                    }}
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={faImage}
                    className="placeholder-icon"
                  />
                )}
              </div>

              <div className="product-info">
                <div className="product-name">
                  {product.name || "Unknown Product"}
                  <span
                    className={`origin-badge ${isMalaysia ? "malaysia" : "other"}`}
                  >
                    <FontAwesomeIcon icon={faGlobe} /> {origin}
                  </span>
                  {product.category && (
                    <span className="product-category">
                      {product.category.replace(/-/g, " ")}
                    </span>
                  )}
                </div>
                <div className="product-rating">
                  {renderStars(product.rating || 0)}
                  <span className="reviews-count">
                    ({product.reviews || 0} reviews)
                  </span>
                </div>
                {isExpanded && product.description && (
                  <div className="product-description-expanded">
                    {product.description}
                  </div>
                )}
                {isExpanded && product.specifications && (
                  <div className="product-specs-expanded">
                    {Object.entries(product.specifications).map(
                      ([key, value]) => (
                        <span key={key} className="spec-tag">
                          <strong>{key}:</strong> {value}
                        </span>
                      ),
                    )}
                  </div>
                )}
              </div>

              <div className="product-stats">
                <div className="stat-group">
                  <div className="stat-item sales">
                    <FontAwesomeIcon icon={faShoppingCart} />
                    <span>{product.sold || 0} sold</span>
                  </div>
                  <div className="stat-item revenue">
                    <FontAwesomeIcon icon={faDollarSign} />
                    <span>{formatCurrency(revenue)}</span>
                  </div>
                  {product.unit && (
                    <div className="stat-item unit">
                      <FontAwesomeIcon icon={faWeightScale} />
                      <span>{product.unit}</span>
                    </div>
                  )}
                  {isMalaysia && (
                    <div className="stat-item malaysia">
                      <FontAwesomeIcon icon={faGlobe} />
                      <span>{SUPPLIER.country}</span>
                    </div>
                  )}
                </div>
                <div className="stat-bar">
                  <div
                    className="stat-bar-fill"
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  />
                  <span className="stat-percentage">
                    {percentage.toFixed(1)}%
                  </span>
                </div>
              </div>

              <div className="product-expand-icon">
                <FontAwesomeIcon icon={isExpanded ? faStar : faStarEmpty} />
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .top-products {
          background: #ffffff;
          padding: 1.25rem 1.5rem;
          border-radius: 8px;
          border: 1px solid #e5e5e5;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        }

        .top-products-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }

        .header-left { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
        .header-icon { color: #d9a321; font-size: 1.1rem; }
        .header-left h3 { color: #006b2e; margin: 0; font-size: 1rem; font-family: "Montserrat", sans-serif; }
        .product-count { font-size: 0.7rem; color: #5a5a5a; background: #f0f0f0; padding: 0.2rem 0.75rem; border-radius: 12px; }

        .header-right { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
        .origin-summary { font-size: 0.65rem; color: #5a5a5a; display: flex; align-items: center; gap: 0.25rem; flex-wrap: wrap; }
        .origin-tag { padding: 0.1rem 0.4rem; border-radius: 8px; background: #f0f0f0; font-size: 0.6rem; }
        .origin-tag.malaysia { background: #dcfce7; color: #16a34a; }
        .supplier-badge { font-size: 0.55rem; padding: 0.1rem 0.5rem; border-radius: 10px; background: #fef3c7; color: #d97706; display: inline-flex; align-items: center; gap: 0.2rem; }

        .sort-controls {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
          padding: 0.5rem 0;
          border-bottom: 1px solid #f0f0f0;
          margin-bottom: 0.75rem;
        }

        .sort-label { font-size: 0.7rem; color: #5a5a5a; }
        .sort-btn {
          padding: 0.15rem 0.5rem;
          border: 1px solid #e5e5e5;
          border-radius: 12px;
          background: #fff;
          cursor: pointer;
          font-size: 0.65rem;
          color: #5a5a5a;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.15rem;
        }
        .sort-btn:hover { background: #f0f0f0; }
        .sort-btn.active { background: #d9a321; color: #fff; border-color: #d9a321; }

        .products-list { display: flex; flex-direction: column; gap: 0.5rem; }

        .product-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          background: #f8f9fa;
          transition: all 0.3s ease;
          cursor: pointer;
          border: 1px solid transparent;
        }

        .product-item:hover { background: #f0f0f0; border-color: #e5e5e5; }
        .product-item.expanded { background: #e8f5e9; border-color: #c8e6c9; }
        .product-item.malaysia-origin { border-left: 3px solid #d9a321; }

        .product-rank { display: flex; flex-direction: column; align-items: center; min-width: 30px; }
        .rank-number { font-weight: 700; font-size: 0.875rem; color: #5a5a5a; }
        .rank-crown { font-size: 0.75rem; }

        .product-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          overflow: hidden;
          background: #f0f0f0;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .product-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .placeholder-icon { color: #d9a321; font-size: 1.25rem; }

        .product-info { flex: 1; min-width: 0; }
        .product-name { font-weight: 600; color: #1f1f1f; display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }

        .origin-badge { font-size: 0.55rem; padding: 0.1rem 0.4rem; border-radius: 8px; }
        .origin-badge.malaysia { background: #dcfce7; color: #16a34a; }
        .origin-badge.other { background: #fef3c7; color: #d97706; }

        .product-category { font-size: 0.6rem; color: #5a5a5a; background: #e5e5e5; padding: 0.1rem 0.5rem; border-radius: 10px; font-weight: 400; text-transform: capitalize; }

        .product-rating { display: flex; align-items: center; gap: 0.15rem; font-size: 0.7rem; flex-wrap: wrap; }
        .star { font-size: 0.7rem; }
        .star.filled { color: #f59e0b; }
        .star.half-filled { color: #f59e0b; }
        .star.empty { color: #d1d5db; }
        .no-rating { color: #9ca3af; font-size: 0.65rem; }
        .rating-value { color: #1f1f1f; font-weight: 600; margin-left: 0.2rem; }
        .reviews-count { color: #9ca3af; font-size: 0.65rem; margin-left: 0.2rem; }

        .product-description-expanded {
          margin-top: 0.5rem;
          padding-top: 0.5rem;
          border-top: 1px solid #e5e5e5;
          font-size: 0.75rem;
          color: #5a5a5a;
          line-height: 1.5;
        }

        .product-specs-expanded {
          display: flex;
          flex-wrap: wrap;
          gap: 0.25rem 0.5rem;
          margin-top: 0.5rem;
          padding-top: 0.5rem;
          border-top: 1px solid #e5e5e5;
        }

        .spec-tag { font-size: 0.65rem; color: #5a5a5a; background: #f0f0f0; padding: 0.1rem 0.4rem; border-radius: 4px; }
        .spec-tag strong { color: #1f1f1f; }

        .product-stats {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.25rem;
          min-width: 120px;
        }

        .stat-group { display: flex; gap: 0.75rem; font-size: 0.7rem; flex-wrap: wrap; }
        .stat-item { display: flex; align-items: center; gap: 0.25rem; color: #5a5a5a; }
        .stat-item svg { font-size: 0.65rem; }
        .stat-item.sales { color: #3b82f6; }
        .stat-item.revenue { color: #10b981; }
        .stat-item.unit { color: #8b5cf6; }
        .stat-item.malaysia { color: #006B2E; }

        .stat-bar {
          width: 100%;
          height: 4px;
          background: #e5e5e5;
          border-radius: 2px;
          position: relative;
          overflow: hidden;
        }
        .stat-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #d9a321, #b8860b);
          border-radius: 2px;
          transition: width 0.6s ease;
        }
        .stat-percentage {
          position: absolute;
          right: 0;
          top: -14px;
          font-size: 0.6rem;
          color: #5a5a5a;
          font-weight: 500;
        }

        .product-expand-icon { color: #9ca3af; font-size: 0.7rem; transition: transform 0.3s ease; }
        .product-item.expanded .product-expand-icon { transform: rotate(180deg); color: #d9a321; }

        .top-products-loading, .top-products-empty {
          background: #ffffff;
          padding: 2rem;
          border-radius: 8px;
          border: 1px solid #e5e5e5;
          text-align: center;
        }
        .top-products-loading { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; color: #5a5a5a; }
        .top-products-loading svg { color: #d9a321; }
        .empty-icon { color: #d1d5db; margin-bottom: 1rem; }
        .top-products-empty h3 { color: #1f1f1f; margin-bottom: 0.25rem; }
        .top-products-empty p { color: #9ca3af; font-size: 0.875rem; }

        @media (max-width: 768px) {
          .top-products { padding: 1rem; }
          .product-item { flex-wrap: wrap; padding: 0.5rem 0.75rem; }
          .product-stats { flex: 1; min-width: auto; align-items: flex-start; margin-left: 60px; }
          .stat-group { flex-wrap: wrap; }
          .product-rank { min-width: 24px; }
          .product-avatar { width: 36px; height: 36px; }
          .product-name { font-size: 0.875rem; }
          .stat-bar { width: 100%; }
          .product-expand-icon { display: none; }
          .top-products-header { flex-direction: column; align-items: stretch; }
          .header-right { justify-content: flex-start; }
          .sort-controls { gap: 0.25rem; }
          .sort-btn { font-size: 0.6rem; padding: 0.1rem 0.4rem; }
        }

        @media (max-width: 480px) {
          .product-item { flex-direction: column; align-items: stretch; }
          .product-stats { margin-left: 0; }
          .stat-group { justify-content: space-between; }
          .product-info { text-align: center; }
          .product-rating { justify-content: center; }
          .product-name { justify-content: center; }
          .product-avatar { align-self: center; }
        }
      `}</style>
    </div>
  );
};

export default TopProducts;
